import { Popover } from "@xola/ui-kit";
import { RowNode } from "ag-grid-community";
import clsx from "clsx";
import { DemographicIcon } from "common";
import { keys, sum } from "lodash";
import React from "react";
import { getDemographicLineItems } from "../../../../models/PurchaseItem";
import { CatalogInfo } from "../RosterGuestTypes";
import { RowData } from "./types";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { isDefaultDemographic } from "@/models/DemographicUnit";

export const demographicCellValueGetter = ({ data }: { data: RowData }) => {
    let totalQuantity = 0;

    if ("demographics" in data) {
        for (const value of Object.values(data.demographics.value)) {
            if (typeof value === "number") {
                totalQuantity += value;
            }
        }

        return totalQuantity;
    }

    for (const demographic of getDemographicLineItems(data.item)) {
        totalQuantity += demographic.quantity ?? 0;
    }

    return totalQuantity;
};

export const DemographicCellRenderer = ({ data, node }: { data: RowData; node: RowNode }) => {
    // Header Expanded Cell
    if (!node.expanded && node.key === "summary" && "demographics" in data) {
        return (
            <div>
                <DemographicsPopover demographics={data.demographics} />
            </div>
        );
    }

    // Header Non-Expanded Cell
    if (data && "demographics" in data) {
        return (
            <ul className="mb-2 mt-2 leading-5">
                {keys(data.demographics.value).map((name: string) => (
                    <li
                        key={name}
                        className="flex items-center space-x-1"
                        data-testid="roster-guest-list-demographics-item"
                    >
                        <div className="w-[120px]">
                            <AutoTooltipContainer tooltipContent={<DemographicName name={name} />}>
                                <span>
                                    <span>{data.demographics.value[name]} </span>
                                    <DemographicName name={name} />
                                </span>
                            </AutoTooltipContainer>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    // Guest Cell
    if (data?.demographic) {
        return (
            <div className="mt-1 mb-1 w-[120px]">
                <AutoTooltipContainer tooltipContent={<DemographicName name={data.demographic.name} />}>
                    <DemographicIcon name={data.demographic.name ?? ""} className="mr-1 flex-shrink-0" />
                    <DemographicName name={data.demographic.name ?? ""} />
                </AutoTooltipContainer>
            </div>
        );
    }

    // Organizer Cell, and the equipment roster's "Attendees" section row, which summarises the same counts
    if (data && "itemDemographics" in data && (data.isOrganizer || data.rosterSection === "attendees")) {
        return (
            <ul className="mb-2 mt-2 leading-5">
                {keys(data.itemDemographics?.value ?? {}).map((name: string) => (
                    <li
                        key={name}
                        className="flex items-center space-x-1 truncate"
                        data-testid="roster-guest-list-demographics-item"
                    >
                        <div className="w-[120px]">
                            <AutoTooltipContainer tooltipContent={<DemographicName name={name} />}>
                                <span>
                                    <span>{data.itemDemographics?.value?.[name]} </span>
                                    <DemographicName name={name} />
                                </span>
                            </AutoTooltipContainer>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    return null;
};

interface DemographicsPopoverProps {
    demographics: CatalogInfo;
    testId?: string;
    className?: string;
    unitLabel?: string;
}

export const DemographicsPopover = ({
    demographics,
    testId = "",
    className,
    unitLabel = "Guests",
}: DemographicsPopoverProps) => {
    const demographicsCountValues = Object.values(demographics?.value ?? {});
    const totalCount = sum(demographicsCountValues);

    return (
        <Popover appendTo={document.body} disabled={totalCount === 0}>
            <span className="underline decoration-dotted" data-testid={testId}>
                <NoTranslate>{totalCount}</NoTranslate> {unitLabel}
            </span>
            <Popover.Content>
                <div className="min-w-[160px] p-3">
                    <ul className={clsx("leading-5", className)}>
                        {keys(demographics.value).map((name: string) => (
                            <li key={name} className="flex items-center justify-between space-x-4">
                                <span className="flex items-center space-x-2">
                                    <DemographicIcon name={name ?? ""} className="flex-shrink-0" />
                                    <DemographicName name={name ?? ""} />
                                </span>
                                <NoTranslate>{demographics.value?.[name]}</NoTranslate>
                            </li>
                        ))}
                    </ul>
                    <hr className="mt-2 mb-2 text-gray-light" />
                    <span className="flex justify-between font-bold">
                        <span>Total</span>
                        <NoTranslate>{totalCount}</NoTranslate>
                    </span>
                </div>
            </Popover.Content>
        </Popover>
    );
};

const DemographicName = ({ name }: { name: string }) => {
    return <span className={clsx(!isDefaultDemographic(name) && "no-translate")}>{name}</span>;
};
